import {
  ApolloProvider,
  ErrorResponse,
  useApollo,
} from '@collinsonx/utils/apolloBooking';
import { UserProvider } from '@collinsonx/utils/lib/userContext';
import SuperTokensReact, {
  SuperTokensConfig,
  SuperTokensWrapper,
} from '@collinsonx/utils/supertokens';
import AuthWrapper from '@components/AuthWrapper';
import GlobalErrorBoundary from '@components/GlobalErrorBoundary';
import MantineWrapper from '@components/MantineWrapper';
import OptimizelyWrapper from '@components/OptimizelyWrapper';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { superTokensConfig } from 'config/supertokens';
import BookingProvider from 'context/BookingContext';
import ErrorProvider from 'context/ErrorContext';
import ProductProvider from 'context/ProductContext';
import { PayloadProvider } from 'hooks/payload';
import getConfig from 'next/config';
import React, { createContext, useRef } from 'react';
import { RenderElement } from 'types/components';
import Observable from 'utils/observer';

type DomainContextProps = object;

const defaultContextValue = {};

export const DomainContext =
  createContext<DomainContextProps>(defaultContextValue);

// we only want to call this init function on the frontend, so
// we check typeof window !== 'undefined'
if (typeof window !== 'undefined') {
  const isInIframe = window.parent !== window;

  SuperTokensReact.init(superTokensConfig({ isInIframe }) as SuperTokensConfig);
}
const { publicRuntimeConfig } = getConfig();
const version = publicRuntimeConfig?.version;

// Set in Vercel this variable for any environments that need monitoring. Prod and probably UAT
const datadogenv: string | undefined = process.env.NEXT_PUBLIC_DATADOG_ENV;
if ((datadogenv?.length ?? 0) > 0) {
  datadogLogs.init({
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
    env: process.env.NEXT_PUBLIC_DATADOG_ENV ?? '',
    forwardErrorsToLogs: true,
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? '',
    sessionSampleRate: 100,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE ?? '',
  });

  datadogRum.init({
    actionNameAttribute: 'data-custom-name',
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID ?? '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
    defaultPrivacyLevel: 'mask-user-input',
    enableExperimentalFeatures: ['feature_flags'],
    env: process.env.NEXT_PUBLIC_DATADOG_ENV ?? '',
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? '',
    sessionReplaySampleRate: 100,
    sessionSampleRate: 100,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE ?? '',
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: version ?? 'n/a',
  });

  datadogRum.startSessionReplayRecording();
}

const DomainProvider: React.FC<{
  children: RenderElement;
  pageProps: object;
}> = ({ children, pageProps }) => {
  const apolloErrorObservable = useRef(new Observable<ErrorResponse>());
  const apolloClient = useApollo(pageProps, apolloErrorObservable.current);

  return (
    <DomainContext.Provider value={defaultContextValue}>
      <UserProvider>
        <SuperTokensWrapper>
          <ApolloProvider client={apolloClient}>
            <AuthWrapper>
              <PayloadProvider>
                <MantineWrapper>
                  <OptimizelyWrapper>
                    <ErrorProvider>
                      <ProductProvider>
                        <BookingProvider>
                          <GlobalErrorBoundary>{children}</GlobalErrorBoundary>
                        </BookingProvider>
                      </ProductProvider>
                    </ErrorProvider>
                  </OptimizelyWrapper>
                </MantineWrapper>
              </PayloadProvider>
            </AuthWrapper>
          </ApolloProvider>
        </SuperTokensWrapper>
      </UserProvider>
    </DomainContext.Provider>
  );
};

export default DomainProvider;
